import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Autocomplete,
    Box,
    Divider,
    FormHelperText,
    Paper,
    TextField,
    Typography,
} from "@mui/material";

import CloseButton from "../../../Components/CloseButton";
import CustomIcon from "../../../Components/CustomIcon";
import {
    StyledDialog,
    StyledTextField,
} from "../../../Components/EditVehicleDetails/EditDialogShared";
import {
    CancelFormActionButton,
    PrimaryFormLoadingActionButton,
} from "../../../Features/Table/GenericDraggableTable/TableStyles";
import { updateAsset } from "../../../Api/AssetApi";
import { getNetworkEntities } from "../../../Api/NetworkEntityApi";
import SingleDatePicker from "../../../Helper/DatePicker/SingleDatePicker";
import { getDatePickerDate } from "../../../Helper/DatePicker/DateConverter";
import CODE from "../../../Static/Constants/StatusCodes";
import { useToast } from "../../../Hooks/useToast";
import {
    assetDateFromPicker,
    buildAssetPayload,
    mapAssetServerErrors,
    validateAssetForm,
} from "./assetTableHelpers";
import { FORM_TOKENS, WORK_ORDER_MENU } from "../WorkOrders/WorkOrderFormShared";

// Edit one asset.
//
// THERE IS NO CREATE PATH. An asset enters the system through a Vendor Intake
// work order and only through one: registering a serial from the Assets table
// wrote a row with no state, no custody and no movement behind it -- a fact
// about an asset that nobody can explain afterwards, and one the lifecycle
// timeline has no row for. The "+ Register Asset" button and this dialog's
// create branch went together; a dialog that can still be opened with no
// asset would be that button coming back through the side door, so it refuses
// to render without one.
//
// WHAT IS NOT ON THIS FORM, AND WHY. currentStateCode, currentEntityID and
// receiptCount are a CACHE of assetLifecycleRecord. The backend's request
// schemas do not merely ignore them -- they declare them so they can refuse
// them with a message saying so (_rejectCacheField) -- because state and
// custody move only when a checker approves a work order, in the same
// transaction as the lifecycle row they mirror. A form field for them would be
// a control that cannot work.
//
// Registering here therefore writes NO lifecycle row: a serial that arrived
// outside a work order has no movement to record, and inventing one would put
// a fact in the history that never happened. The asset is born stateless and
// held by nobody, and is counted under the "Not yet moved" badge until its
// first approved work order.
//
// assetTypeCode has NO FIELD. The platform supports one asset type and the
// server owns it -- it is immutable on PUT besides -- so the form sends
// nothing at all rather than repeating that constant in JS. The value the
// server stored is still shown, in the Asset Type column and in the detail
// view; it is just not something a user types.
//
// Chrome is deliberately NOT bespoke: StyledDialog and StyledTextField are the
// same components the Edit Vehicle Details flow and CreateEditEntityDialog
// use, so the three dialogs read as one product.

const AUTOCOMPLETE_INPUT_SX = {
    "& .MuiOutlinedInput-root": {
        fontSize: "14px",
        borderRadius: "8px",
        backgroundColor: FORM_TOKENS.surface,
        // The right padding is NOT decoration: MUI reserves room for the clear
        // and expand buttons by padding this root element, and a blanket
        // override removes that reservation -- the value then runs underneath
        // the buttons and is clipped mid-character instead of ellipsing.
        padding: "4px 56px 4px 8px !important",
        "& fieldset": { borderColor: FORM_TOKENS.fieldBorder, borderWidth: "1px" },
        "&:hover fieldset": { borderColor: FORM_TOKENS.purple, borderWidth: "1px" },
        "&.Mui-focused fieldset": { borderColor: FORM_TOKENS.purple, borderWidth: "1px" },
        "& input": {
            fontSize: "14px",
            padding: "6px 4px !important",
            textOverflow: "ellipsis",
        },
        "& input::placeholder": { color: FORM_TOKENS.placeholder, opacity: 1, fontSize: "14px" },
    },
};

const AUTOCOMPLETE_SX = {
    "& .MuiAutocomplete-endAdornment": {
        top: "50% !important",
        transform: "translateY(-50%) !important",
    },
};

// How many vendors the picker holds. The list endpoint caps `limit` at 100, and
// this control has no server-side search, so an org with more vendors than this
// sees the first page. Worth knowing before it bites: the fix is a typeahead,
// not a bigger number.
const VENDOR_PAGE_LIMIT = 100;

const DropdownPaper = (props) => (
    <Paper
        {...props}
        sx={{
            borderRadius: "8px",
            boxShadow: WORK_ORDER_MENU.shadow,
            "& .MuiAutocomplete-option": { fontSize: "14px" },
        }}
    />
);

// Label above the control, never a floating MUI label -- the required marker is
// a real red asterisk beside the text, matching every Edit Vehicle view.
const FieldLabel = ({ label, required }) => (
    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
        <Typography variant="fw400s14c000000" sx={{ lineHeight: "24px", color: FORM_TOKENS.inkBody }}>
            {label}
        </Typography>
        {required && (
            <Typography
                variant="fw400s14c000000"
                sx={{ fontWeight: 700, lineHeight: "24px", color: FORM_TOKENS.required }}
            >
                &nbsp;*
            </Typography>
        )}
    </Box>
);

const FieldHelper = ({ text, isError }) =>
    text ? (
        <FormHelperText
            sx={{
                fontSize: "10px",
                color: isError ? FORM_TOKENS.danger : FORM_TOKENS.inkFaint,
                ml: 0,
                mt: 0.25,
                overflowWrap: "anywhere",
            }}
        >
            {text}
        </FormHelperText>
    ) : null;

const EMPTY_VALUES = {
    serialNumber: "",
    currentLoanReferenceID: "",
    manufacturingDate: "",
    sourceVendorEntityID: "",
};

// The network entity type that holds vendors. Read from the SAME
// entityDefinition the Network tab renders, rather than hardcoded, because the
// set of types is per-org config. An org with no vendor type simply does not
// get the field -- the column is nullable and the rest of the form still works.
const findVendorTypeCode = (metaInfo) => {
    const types = metaInfo?.network?.entityDefinition?.types || [];
    const match = types.find((type) => /vendor/i.test(type?.code || ""));
    return match?.code || null;
};

const CreateEditAssetDialog = ({ open, asset, metaInfo, onClose, onSuccess }) => {
    const [values, setValues] = useState(EMPTY_VALUES);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [saving, setSaving] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [vendorsError, setVendorsError] = useState("");
    const { openNotification } = useToast();

    const vendorTypeCode = useMemo(() => findVendorTypeCode(metaInfo), [metaInfo]);

    // Seed the form whenever the dialog opens. The asset arrives FLAT -- core
    // columns and custom attributes already merged by the API -- so one lookup
    // per key works for both kinds.
    useEffect(() => {
        if (!open) return;
        setValues({
            serialNumber: asset?.serialNumber ?? "",
            currentLoanReferenceID: asset?.currentLoanReferenceID ?? "",
            // The API returns a date as YYYY-MM-DD and takes it back in the
            // same shape, so that string is what form state holds end to end.
            // SingleDatePicker works in dayjs; the conversion happens at the
            // picker itself (assetDateFromPicker), not here.
            manufacturingDate: asset?.manufacturingDate ?? "",
            sourceVendorEntityID: asset?.sourceVendorEntityID ?? "",
        });
        setErrors({});
        setTouched({});
    }, [open, asset]);

    // Fetched once per open, and only when the org actually has a vendor type.
    useEffect(() => {
        if (!open || !vendorTypeCode) return undefined;
        let cancelled = false;
        setVendorsError("");
        (async () => {
            const res = await getNetworkEntities({
                entityTypeCode: vendorTypeCode,
                page: 1,
                limit: VENDOR_PAGE_LIMIT,
            });
            if (cancelled) return;
            if (res?.responseStatus?.code === CODE.SUCCESS) {
                const rows = res?.data?.records || [];
                setVendors(Array.isArray(rows) ? rows : []);
            } else {
                // A failure must be distinguishable from an empty list, or a
                // 403 reads as "no vendors are configured".
                setVendors([]);
                setVendorsError(res?.responseStatus?.msg || "Could not load vendors");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, vendorTypeCode]);

    const vendorOptions = useMemo(
        () =>
            vendors
                .filter((vendor) => vendor?.id)
                .map((vendor) => ({
                    id: vendor.id,
                    label: vendor.name || vendor.entityCode || `#${vendor.id}`,
                })),
        [vendors]
    );

    const handleChange = useCallback((key, value) => {
        setValues((prev) => ({ ...prev, [key]: value }));
        // Clear the error as the user types; re-check on blur. Validating every
        // keystroke flags "Serial number is required" on the first character.
        setErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
    }, []);

    const handleBlur = useCallback(
        (key) => {
            setTouched((prev) => ({ ...prev, [key]: true }));
            // Re-run the whole form and keep only this field's verdict: the
            // rules live in one exported function, so there is no per-field
            // copy of them here to drift.
            const formErrors = validateAssetForm(values);
            setErrors((prev) => ({ ...prev, [key]: formErrors[key] || "" }));
        },
        [values]
    );

    const markEverythingTouched = useCallback(
        () =>
            setTouched(
                Object.keys(EMPTY_VALUES).reduce(
                    (acc, key) => ({ ...acc, [key]: true }),
                    {}
                )
            ),
        []
    );

    const handleSubmit = async () => {
        const localErrors = validateAssetForm(values);
        if (Object.keys(localErrors).length) {
            setErrors(localErrors);
            markEverythingTouched();
            return;
        }

        setSaving(true);
        try {
            const payload = buildAssetPayload(values);
            const res = await updateAsset(asset.id, payload);

            if (
                res?.responseStatus?.code === CODE.SUCCESS ||
                res?.responseStatus?.code === CODE.URL_CREATED
            ) {
                openNotification({
                    text: "Asset updated successfully",
                    icon: "toast-success",
                });
                onSuccess?.();
                return;
            }

            // A 400 or 409 carries data.errors keyed by field -- a duplicate
            // serial being the one every user will meet. Painting them onto the
            // form is the whole reason this API keeps the response body on
            // failure; see Api/AssetApi.js.
            const serverErrors = mapAssetServerErrors(res);
            if (Object.keys(serverErrors).length) {
                setErrors(serverErrors);
                markEverythingTouched();
            } else {
                openNotification({
                    text: res?.responseStatus?.msg || "Could not save the asset",
                    icon: "toast-error",
                });
            }
        } finally {
            setSaving(false);
        }
    };

    const errorFor = (key) => (touched[key] ? errors[key] || "" : "");

    const renderTextField = ({ key, label, required, helpText }) => {
        const error = errorFor(key);
        return (
            <Box sx={{ minWidth: 0 }}>
                <FieldLabel label={label} required={required} />
                <StyledTextField
                    fullWidth
                    autoComplete="off"
                    variant="outlined"
                    placeholder="Type here"
                    inputProps={{ "data-testid": `asset-field-${key}` }}
                    value={values[key] ?? ""}
                    disabled={saving}
                    error={Boolean(error)}
                    required={Boolean(required)}
                    onChange={(event) => handleChange(key, event.target.value)}
                    onBlur={() => handleBlur(key)}
                />
                <FieldHelper text={error || helpText || ""} isError={Boolean(error)} />
            </Box>
        );
    };

    // The platform's date control, not `<input type="date">`: the native
    // picker is the browser's chrome and matches nothing else in the product.
    //
    // The value contract is unchanged -- form state and the payload still hold
    // YYYY-MM-DD -- and the dayjs boundary is crossed in exactly one place,
    // assetDateFromPicker.
    const renderManufacturingDateField = () => {
        const error = errorFor("manufacturingDate");
        return (
            <Box sx={{ minWidth: 0 }}>
                <FieldLabel label="Manufactured On" />
                <SingleDatePicker
                    dataTestId="asset-field-manufacturingDate"
                    // Controlled: the dialog seeds this from the asset when it
                    // opens, and an uncontrolled picker would keep showing the
                    // placeholder while form state already held the date.
                    value={values.manufacturingDate || null}
                    handleOnChange={(date) =>
                        handleChange("manufacturingDate", assetDateFromPicker(date))
                    }
                    // Nothing is manufactured in the future. The ORG's today
                    // (getDatePickerDate reads the timeZone cookie), not the
                    // browser's: a support user in another zone would otherwise
                    // have the org's real today greyed out.
                    //
                    // A bound, not a validation rule -- an asset already
                    // carrying a future date from a bad import still opens and
                    // still saves, rather than locking every unrelated edit
                    // behind a field the user did not touch.
                    maxDate={getDatePickerDate()}
                    placeholder="Select date"
                    status={error ? "error" : ""}
                    disabled={saving}
                    singleIcon
                    fullWidth
                />
                <FieldHelper text={error || ""} isError={Boolean(error)} />
            </Box>
        );
    };

    const renderVendorField = () => {
        const error = errorFor("sourceVendorEntityID");
        const selected =
            vendorOptions.find((option) => option.id === values.sourceVendorEntityID) ||
            null;
        const note =
            vendorsError ||
            (vendorOptions.length ? "" : "No vendors are configured yet");

        return (
            <Box sx={{ minWidth: 0 }}>
                <FieldLabel label="Source Vendor" />
                <Autocomplete
                    fullWidth
                    disabled={saving}
                    options={vendorOptions}
                    value={selected}
                    getOptionLabel={(option) => option?.label ?? ""}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    onChange={(_, option) =>
                        handleChange("sourceVendorEntityID", option ? option.id : "")
                    }
                    onBlur={() => handleBlur("sourceVendorEntityID")}
                    sx={AUTOCOMPLETE_SX}
                    PaperComponent={DropdownPaper}
                    slotProps={{ listbox: { sx: { fontSize: "14px" } } }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Search or select a vendor"
                            error={Boolean(error)}
                            inputProps={{
                                ...params.inputProps,
                                "data-testid": "asset-field-sourceVendorEntityID",
                            }}
                            sx={AUTOCOMPLETE_INPUT_SX}
                        />
                    )}
                />
                <FieldHelper
                    text={error || note || "Where this serial came from, if it is known"}
                    isError={Boolean(error)}
                />
            </Box>
        );
    };

    // No asset is not an empty form any more -- it is a caller mistake, and
    // rendering a blank dialog would put the removed create path back on
    // screen with a Save button that would 404 on PUT /api/assets/undefined.
    if (!asset?.id) return null;

    return (
        <StyledDialog open={open} onClose={() => !saving && onClose()}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CustomIcon name="edit-icon" />
                    <Typography
                        variant="fw600s16c000000"
                        sx={{ fontSize: "20px", lineHeight: "20px", color: FORM_TOKENS.inkStrong }}
                    >
                        Edit Asset
                    </Typography>
                </Box>
                <CloseButton onClick={() => !saving && onClose()} />
            </Box>

            <Divider sx={{ my: 3, borderColor: FORM_TOKENS.fieldBorder }} />

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    columnGap: 2,
                    rowGap: 2,
                    mb: 1.5,
                }}
            >
                {renderTextField({
                    key: "serialNumber",
                    label: "Serial Number",
                    required: true,
                    helpText: "Unique within the organisation, even after deletion",
                })}
                {renderTextField({
                    key: "currentLoanReferenceID",
                    label: "Loan ID",
                })}
                {renderManufacturingDateField()}
                {vendorTypeCode ? renderVendorField() : null}
            </Box>

            <Box sx={{ mb: 3 }} />

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <CancelFormActionButton
                    data-testid="asset-cancel"
                    onClick={onClose}
                    disabled={saving}
                >
                    Cancel
                </CancelFormActionButton>
                <PrimaryFormLoadingActionButton
                    data-testid="asset-save"
                    onClick={handleSubmit}
                    loading={saving}
                    disabled={saving}
                >
                    Save Changes
                </PrimaryFormLoadingActionButton>
            </Box>
        </StyledDialog>
    );
};

export default CreateEditAssetDialog;
